<template>
  <div v-if="workspace.org || workspace.tenant">
    <SCADropdown
      v-model:is-open="isOpen"
      :class="$style.dropdown"
      :offset-y="offsetY"
      :offset-x="offsetX"
      no-border
      no-padding
      shadow-variant="upward"
    >
      <template #trigger="{ target }">
        <SCAWorkspaceItem
          :class="$style.trigger"
          :popovertarget="target"
          :title="displayTitle"
          :subtitle="displaySubtitle"
          :icon="currentIcon"
          :expandable="isExpandable"
          :expanded="isOpen"
          :focused="isOpen"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        />
      </template>
      <template #default>
        <div :class="$style.content">
          <template v-for="(tenant, index) in workspace.selectableTenants" :key="tenant.id">
            <div :class="$style.tenantWrapper">
              <TenantTreeItem
                :selected-org="workspace.orgId"
                :tenant="tenant"
                :org-tree="getOrgTreeForTenant(tenant, workspace.tenantOrgTrees)"
                :expand-all="tenant.id === workspace.tenantId"
                @close="isOpen = false"
                @update:selected-org="handleOrgSelect($event, tenant.id)"
              />
            </div>
            <SCADivider
              v-if="index < (workspace.selectableTenants?.length ?? 0) - 1"
              direction="horizontal"
              appearance="border"
            />
          </template>
        </div>
      </template>
    </SCADropdown>
  </div>
</template>
<script setup lang="ts">
import { SCADivider, SCADropdown, SCAWorkspaceItem } from "@scayla/components";
import { useScaBreakpoints } from "@scayla/core";
import { useMediaQuery } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import TenantTreeItem from "@/components/navigation/tenantOrgaSelect/TenantTreeItem.vue";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";
import { resolveRecordReturnTo } from "@/utils/recordReturnTo";

import { getDisplayTitle, getIcon, getLabel, getOrgTreeForTenant } from "./tenantOrgaSelect/tenantOrgaUtils";

const workspace = useWorkspace();
const router = useRouter();
const route = useRoute();

const { isMobile } = useScaBreakpoints();
const isHovered = ref(false);
const isOpen = ref(false);

const hasHoverCapability = useMediaQuery("(hover: hover)");

const isExpandable = computed(() => isOpen.value || (hasHoverCapability.value && isHovered.value));

function handleMouseEnter() {
  if (hasHoverCapability.value) {
    isHovered.value = true;
  }
}

function handleMouseLeave() {
  if (hasHoverCapability.value) {
    isHovered.value = false;
  }
}

const offsetY = computed(() => (isMobile.value ? 0 : 12));
const offsetX = -8;

const orgTree = computed(() => getOrgTreeForTenant(workspace.tenant, workspace.tenantOrgTrees));
const isTenantSelected = computed(() => !!workspace.tenantId && !workspace.orgId);

const currentIcon = computed(() => getIcon(isTenantSelected.value, workspace.tenant, workspace.org));
const displaySubtitle = computed(() => {
  const isActuallyTenant = !workspace.orgId;
  return getLabel(isActuallyTenant, workspace.tenant, isActuallyTenant ? null : workspace.org);
});
const displayTitle = computed(() =>
  getDisplayTitle(isTenantSelected.value, orgTree.value, workspace.tenant, workspace.org, workspace.orgId),
);

const handleOrgSelect = (orgId: string | undefined, tenantId: string) => {
  const tenantChanged = tenantId !== workspace.tenantId;
  const orgChanged = orgId !== workspace.orgId;
  workspace.setTenantAndOrg(tenantId, orgId);
  isOpen.value = false;
  if (tenantChanged) {
    void router.push({ name: Routes.HOME, query: { tenant: tenantId } });
  } else if (orgChanged && typeof route.params.recordId === "string") {
    const { moduleId } = route.params;
    const returnTo = typeof route.query.returnTo === "string" ? route.query.returnTo : undefined;
    void router.push(resolveRecordReturnTo(router, route, moduleId as string, returnTo));
  }
};
</script>
<style lang="scss" module>
.dropdown {
  display: flex;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-medium);
  width: 304px;
  max-height: calc(
    100vh - var(--sca-main-header-height) - var(--sca-sub-header-height) - var(--space-component-padding-medium) * 3
  );
  overflow-x: hidden;
  scrollbar-width: auto;
}

.trigger {
  flex-grow: 1;

  .label {
    display: flex;
    align-items: center;
    gap: var(--space-component-gap-medium);
  }

  .avatar {
    display: none;
    @screen sm {
      display: flex;
    }
  }
}
</style>
